/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { ProductSearchResult, Scope, SearchVariables } from '@/plp/data/models';
import { fetchGraphQl } from '@/plp/api';
import { transformProductSearchResponse } from '@/plp/data/transforms';
import { events } from '@adobe-commerce/event-bus';
import { PRODUCT_SEARCH_QUERY } from './graphql/search.graphql';
import { 
  searchRequestSent, 
  searchResponseReceived, 
  updateSearchInputCtx,
  updateSearchResultsCtx,
  SEARCH_UNIT_ID, 
  PLP_UNIT_ID,
} from '@/plp/utils/acdlEvents';

type SearchOptions = {
  scope?: Scope;
};

export const search = async (
  request: SearchVariables | null,
  options: SearchOptions = {},
): Promise<ProductSearchResult> => {
  const scope = options.scope === 'search' ? undefined : options.scope;
  // empty result
  const emptyResult = {
    request: request || {} ,
    result: {
      facets: [],
      pageInfo: { currentPage: 0, totalPages: 0, totalItems: 0, pageSize: 0 },
      items: [],
      totalCount: 0,
      suggestions: [],
      metadata: {
        filterableAttributes: [],
        sortableAttributes: []
      }
    }
  };

  // clear search results if no request are provided
  if (request === null) {
    events.emit('search/result', emptyResult, { scope });
    return emptyResult.result;
  }


  // Emit loading state
  events.emit('search/loading', true, { scope });

  try {
    const acdlUnitId = scope === 'popover' ? SEARCH_UNIT_ID : PLP_UNIT_ID;
    const searchRequestId = window.crypto.randomUUID();

    const resolvedRequest: SearchVariables = {
      ...request,
      phrase: request.phrase ?? '',
      currentPage: request.currentPage ?? 1,
      sort: request.sort ?? [],
      filter: request.filter ?? [],
    };

    updateSearchInputCtx(
      acdlUnitId,
      searchRequestId,
      resolvedRequest.phrase || '',
      resolvedRequest.filter || [],
      resolvedRequest.pageSize || 0,
      resolvedRequest.currentPage || 0,
      resolvedRequest.sort || [],
    );

    // Publish search request event to ACDL
    searchRequestSent(acdlUnitId);

    // Execute search with validated request
    const { errors, data } = await fetchGraphQl(PRODUCT_SEARCH_QUERY, {
      method: 'GET',
      variables: { ...resolvedRequest },
    });

    if (errors && !data) {
      throw new Error('Error fetching product search');
    }

    const result = transformProductSearchResponse(data, resolvedRequest);

    // Update search results context in ACDL
    updateSearchResultsCtx(acdlUnitId, searchRequestId, result);

    // Publish search response event to ACDL
    searchResponseReceived(acdlUnitId);

    // Emit single event with result and metadata
    events.emit('search/result', { request: resolvedRequest, result }, { scope });

    return result;

  } catch (error) {
    // Emit error event
    events.emit('search/error', (error as Error).message, { scope });

    // Emit result event with empty result
    events.emit('search/result', emptyResult, { scope });

    throw error;
  } finally {
    // Reset loading state
    events.emit('search/loading', false, { scope });
  }
};